import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useEnvironment } from './EnvironmentContext';

const REFERENCE_FX = {
  AUD_IDR: { rate: 10080, change: 2.5 },
  AUD_PHP: { rate: 38.5, change: 1.2 },
  AUD_INR: { rate: 56.2, change: -0.8 },
  AUD_SGD: { rate: 0.87, change: 0.5 },
  AUD_VND: { rate: 16420, change: 1.8 },
  AUD_USD: { rate: 0.66, change: 0.3 },
};

function formatFxMessage(from, to, rate, changePercent) {
  const change = changePercent != null ? changePercent : (REFERENCE_FX[`${from}_${to}`]?.change ?? 2.5);
  if (to === 'IDR') return `AUD to IDR rate has improved by ${change}%. Great time to send money!`;
  const rateStr = rate != null ? Number(rate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 }) : (REFERENCE_FX[`${from}_${to}`]?.rate ?? '—');
  const changeStr = change != null ? ` (${change >= 0 ? '+' : ''}${change}%)` : '';
  return `1 ${from} = ${rateStr} ${to}${changeStr}`;
}

const fallbackList = () => [];

const NotificationsContext = createContext(null);

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationsProvider');
  return ctx;
}

export function NotificationsProvider({ children }) {
  const { api, user } = useAuth();
  const { isLive } = useEnvironment();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const enrichFxMessage = useCallback(async (item) => {
    if (item.type !== 'fx') return item;
    const from = item.fx_from || 'AUD';
    const to = item.fx_to || 'IDR';
    let rate = null, changePercent = null;
    try {
      const res = await api.get(`/fx/rate?from=${from}&to=${to}`);
      rate = res.data?.rate;
      changePercent = res.data?.change_percent;
    } catch (_) {}
    if (rate == null && REFERENCE_FX[`${from}_${to}`]) {
      rate = REFERENCE_FX[`${from}_${to}`].rate;
      changePercent = REFERENCE_FX[`${from}_${to}`].change;
    }
    return { ...item, message: formatFxMessage(from, to, rate, changePercent) };
  }, [api]);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/notifications');
      const data = res.data?.data || [];
      const raw = data.length > 0 ? data : fallbackList();
      const withNew = raw.map((n) => ({ ...n, isNew: !n.read, details: n.details || {} }));
      const fxIndices = withNew.map((n, i) => (n.type === 'fx' ? i : -1)).filter((i) => i >= 0);
      if (fxIndices.length === 0) {
        setNotifications(withNew);
        setLoading(false);
        return;
      }
      const enriched = [...withNew];
      await Promise.all(fxIndices.map(async (i) => { enriched[i] = await enrichFxMessage(enriched[i]); }));
      setNotifications(enriched);
    } catch (_) {
      setNotifications((prev) => {
        const hadAllRead = prev.length > 0 && prev.every((n) => n.read && !n.isNew);
        if (hadAllRead) return prev;
        const fallback = fallbackList().map((n) => ({ ...n, read: true, isNew: false, details: {} }));
        return fallback;
      });
    } finally {
      setLoading(false);
    }
  }, [api, enrichFxMessage]);

  useEffect(() => {
    if (user) fetchNotifications();
    else setLoading(false);
  }, [fetchNotifications, user, isLive]); // Refetch when switching LIVE/Sandbox so merchants see KYC-relevant notifications

  useEffect(() => {
    if (!user) return;
    const interval = setInterval(fetchNotifications, 12000);
    return () => clearInterval(interval);
  }, [fetchNotifications, user]);

  useEffect(() => {
    if (!user) return;
    const onFocus = () => fetchNotifications();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchNotifications, user]);

  const markAsRead = useCallback((id) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true, isNew: false } : n)));
    api.post('/notifications/mark-read', { id }).catch(() => {});
  }, [api]);

  const markAllRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true, isNew: false })));
    try {
      await api.post('/notifications/mark-all-read');
      await fetchNotifications();
    } catch (_) {}
  }, [api, fetchNotifications]);

  const removeNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const unreadCount = notifications.filter((n) => n.isNew ?? !n.read).length;

  const value = {
    notifications,
    loading,
    unreadCount,
    fetchNotifications,
    markAsRead,
    markAllRead,
    removeNotification,
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}
